import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Boxes, Loader2, Search } from 'lucide-react';
import React, { useState } from 'react';
import { PartnerResourceModal } from '../../../components/PartnerResourceModal';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { useTenantApi } from '../../../hooks/useTenantApi';
import type { SchemaDefinition } from '../../../lib/api';
import type { TenantResourceData, TenantResourcesPage } from '../../../lib/tenant-api';
import { CUSTOM_OBJECTS_STRINGS } from './customObjectsStrings';
import styles from './CustomObjectsTable.module.css';
import { CustomObjectRow } from './CustomObjectRow';
import { getExternalIdField, getPreviewFields } from './customObjectSchemas';

const PAGE_SIZE = 20;

const toSingleRecordPage = (resource: TenantResourceData | null): TenantResourcesPage => ({
  results: resource ? [resource] : [],
  pagination: { total: resource ? 1 : 0, hasNextPage: false, cursor: null },
});

interface CustomObjectsTableProps {
  schemaDefinition: SchemaDefinition;
}

export const CustomObjectsTable: React.FC<CustomObjectsTableProps> = ({ schemaDefinition }) => {
  const tenantApi = useTenantApi();
  const [searchInput, setSearchInput] = useState('');
  const [externalId, setExternalId] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageCursors, setPageCursors] = useState<Record<number, string | undefined>>({});
  const [selectedExternalId, setSelectedExternalId] = useState<string | null>(null);

  const applyExternalIdSearch = (nextExternalId: string) => {
    setExternalId(nextExternalId);
    setCurrentPage(1);
    setPageCursors({});
  };

  const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    applyExternalIdSearch(searchInput.trim());
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextSearchInput = event.target.value;
    setSearchInput(nextSearchInput);
    const isSearchCleared = nextSearchInput.trim() === '';
    if (isSearchCleared) {
      applyExternalIdSearch('');
    }
  };

  const schemaDefinitionId = schemaDefinition.id;
  const cursor = pageCursors[currentPage];
  const {
    data: resourcesPage,
    isLoading,
    isFetching,
    error,
  } = useQuery({
    queryKey: tenantApi.queryKey('resources', schemaDefinitionId, externalId, cursor),
    queryFn: async () =>
      externalId
        ? toSingleRecordPage(
            await tenantApi.findResourceByExternalId({ schemaDefinitionId, externalId }),
          )
        : tenantApi.listResources({ schemaDefinitionId, cursor, limit: PAGE_SIZE }),
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  const resources = resourcesPage?.results ?? [];
  const pagination = resourcesPage?.pagination;
  const externalIdField = getExternalIdField(schemaDefinition);
  const previewFields = getPreviewFields(schemaDefinition);
  const displayMetadata = schemaDefinition.display_metadata ?? schemaDefinition.displayMetadata;

  const handlePreviousPage = () => setCurrentPage(page => Math.max(1, page - 1));
  const handleNextPage = () => {
    const nextCursor = pagination?.cursor ?? undefined;
    setPageCursors(cursors => ({ ...cursors, [currentPage + 1]: nextCursor }));
    setCurrentPage(page => page + 1);
  };

  const firstItemNumber = (currentPage - 1) * PAGE_SIZE + 1;
  const lastItemNumber = firstItemNumber + resources.length - 1;
  const paginationSummary = `Viewing ${firstItemNumber}-${lastItemNumber} of ${pagination?.total ?? 0} total records`;
  const pageLabel = `Page ${currentPage}`;
  const emptyDescription = externalId
    ? CUSTOM_OBJECTS_STRINGS.noMatchesDescription
    : CUSTOM_OBJECTS_STRINGS.noRecordsDescription;
  const isFirstPage = currentPage === 1;
  const hasNextPage = pagination?.hasNextPage ?? false;
  const errorMessage = `${CUSTOM_OBJECTS_STRINGS.loadError}: ${error instanceof Error ? error.message : ''}`;

  return (
    <section className={styles.records}>
      <form role="search" className={styles.search} onSubmit={handleSearchSubmit}>
        <Search aria-hidden="true" />
        <Input
          type="search"
          aria-label={CUSTOM_OBJECTS_STRINGS.searchLabel}
          placeholder={CUSTOM_OBJECTS_STRINGS.searchPlaceholder}
          value={searchInput}
          onChange={handleSearchChange}
        />
      </form>

      {error && <p className={styles.error}>{errorMessage}</p>}

      {isLoading && (
        <p className={styles.status}>
          <Loader2 aria-hidden="true" />
          {CUSTOM_OBJECTS_STRINGS.loadingRecords}
        </p>
      )}

      {!isLoading && !error && resources.length === 0 && (
        <article className={styles.emptyState}>
          <Boxes aria-hidden="true" />
          <h4>{CUSTOM_OBJECTS_STRINGS.noRecordsTitle}</h4>
          <p>{emptyDescription}</p>
        </article>
      )}

      {resources.length > 0 && (
        <article className={styles.tableFrame}>
          <div className={styles.tableScroll}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{CUSTOM_OBJECTS_STRINGS.externalIdColumn}</th>
                  {previewFields.map(fieldName => (
                    <th key={fieldName}>{fieldName}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {resources.map(resource => {
                  const resourceExternalId = String(resource[externalIdField]);
                  return (
                    <CustomObjectRow
                      key={resourceExternalId}
                      externalId={resourceExternalId}
                      resource={resource}
                      previewFields={previewFields}
                      displayMetadata={displayMetadata}
                      onSelect={setSelectedExternalId}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>

          <footer className={styles.pagination}>
            {paginationSummary}
            <nav className={styles.pageControls}>
              <Button
                variant="outline"
                size="sm"
                onClick={handlePreviousPage}
                disabled={isFirstPage || isFetching}>
                {CUSTOM_OBJECTS_STRINGS.previousPage}
              </Button>
              {pageLabel}
              <Button
                variant="outline"
                size="sm"
                onClick={handleNextPage}
                disabled={!hasNextPage || isFetching}>
                {CUSTOM_OBJECTS_STRINGS.nextPage}
              </Button>
            </nav>
          </footer>
        </article>
      )}

      {selectedExternalId && (
        <PartnerResourceModal
          resource={{ externalId: selectedExternalId }}
          schemaDefinitionId={schemaDefinitionId}
          isOpen
          onClose={() => setSelectedExternalId(null)}
        />
      )}
    </section>
  );
};
